import moment from 'moment';
import { CampaignResponse, Conditions, Result } from '@models/campaign';
import { ItemResponse } from '@models/order/items/response';
import { GROUP_TYPES } from 'src/enums/group-types';
import {
    ApplyContext,
    ConflictItem,
    FetchContext,
    FetchParams,
    OrderContext,
    SubmitError,
    ValidationError,
} from '../types';
import { CampaignStrategy } from './campaign-strategy';

const CONDITION_FIELD = 'ATINGIR_COTA';
const PAYMENT_CONDITION_BONIFICACAO = '100';

const getConditionItems = (campaign: CampaignResponse): Conditions[] =>
    campaign.condicoes.filter((c) => c.campo === CONDITION_FIELD);

const lineValue = (item: ItemResponse | undefined): number => {
    if (!item) return 0;
    const valor = typeof item.valor === 'number' ? item.valor : Number(item.valor) || 0;
    const quantity = Number(item.quantity) || 0;
    const presentation = Number(item.quantityPresentation) || 1;
    return valor * quantity * presentation;
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

interface ConditionEvaluation {
    minTotal: number;
    achievedTotal: number;
    isMet: boolean;
}

const evaluateCondition = (
    campaign: CampaignResponse,
    orderItems: ItemResponse[],
): ConditionEvaluation => {
    const conditions = getConditionItems(campaign);
    let minTotal = 0;
    let achievedTotal = 0;
    let allConditionItemsHaveQuantity = true;

    for (const cond of conditions) {
        const item = orderItems.find(
            (i) => i.number?.toLowerCase() === cond.numeroItem?.toLowerCase(),
        );
        const gross = Number(item?.valorGross) || 0;
        const percentual = Number(cond.valor) || 0;
        const minItem = gross * (1 + percentual / 100);
        minTotal += minItem;
        achievedTotal += lineValue(item);

        // Cada item de condição deve ter pelo menos quantity > 0,
        // mesmo que o valor mínimo calculado seja 0.
        const qty = Number(item?.quantity) || 0;
        if (qty <= 0) {
            allConditionItemsHaveQuantity = false;
        }
    }

    const valueMet = round2(minTotal) === 0
        ? round2(achievedTotal) > 0
        : round2(achievedTotal) >= round2(minTotal);

    return {
        minTotal: round2(minTotal),
        achievedTotal: round2(achievedTotal),
        isMet: valueMet && allConditionItemsHaveQuantity,
    };
};

export class DescontoAtingirMetaStrategy implements CampaignStrategy {
    readonly tipo = 'DESCONTO_ATINGIR_META' as const;

    isAvailableForOrder(
        _campaign: CampaignResponse,
        context: OrderContext,
    ): boolean {
        if (context.isMarketingBonification) return false;
        return context.orderTypeId === GROUP_TYPES.S1;
    }

    validateEligibility(
        campaign: CampaignResponse,
        availableItems: ItemResponse[],
    ): ValidationError[] {
        const errors: ValidationError[] = [];

        for (const cond of getConditionItems(campaign)) {
            const item = availableItems.find(
                (i) => i.number === cond.numeroItem,
            );
            if (!item) {
                errors.push({
                    code: 'CONDITION_ITEM_MISSING',
                    message: `SKU ${cond.numeroItem} (condição) não disponível`,
                    numeroItem: cond.numeroItem,
                });
            }
        }

        for (const result of campaign.resultados) {
            if (!result.numeroItem) continue;
            const item = availableItems.find(
                (i) => i.number === result.numeroItem,
            );
            if (!item) {
                errors.push({
                    code: 'RESULT_ITEM_MISSING',
                    message: `SKU ${result.numeroItem} (resultado) não disponível`,
                    numeroItem: result.numeroItem,
                });
            }
        }

        return errors;
    }

    detectConflicts(
        campaign: CampaignResponse,
        otherSelected: CampaignResponse[],
    ): ConflictItem[] {
        const conflicts: ConflictItem[] = [];
        const ownNumbers = new Set<string>([
            ...getConditionItems(campaign).map((c) => c.numeroItem),
            ...campaign.resultados.map((r) => r.numeroItem).filter(Boolean),
        ]);

        for (const other of otherSelected) {
            if (other.id === campaign.id) continue;
            const otherNumbers = [
                ...other.condicoes
                    .filter((c) => c.campo === 'QUANTIDADE_ITEM' || c.campo === CONDITION_FIELD)
                    .map((c) => c.numeroItem),
                ...other.resultados.map((r) => r.numeroItem).filter(Boolean),
            ];

            for (const n of otherNumbers) {
                if (ownNumbers.has(n)) {
                    conflicts.push({
                        numeroItem: n,
                        campaignIds: [campaign.id, other.id],
                    });
                }
            }
        }

        return conflicts;
    }

    onEnable(
        campaign: CampaignResponse,
        orderItems: ItemResponse[],
        _context: ApplyContext,
    ): ItemResponse[] {
        const conditions = getConditionItems(campaign);
        if (conditions.length === 0) return orderItems;

        return orderItems.map((item) => {
            const cond = conditions.find(
                (c) => c.numeroItem?.toLowerCase() === item.number?.toLowerCase(),
            );
            if (!cond) return item;

            const gross = Number(item.valorGross) || 0;
            const percentual = Number(cond.valor) || 0;
            const valor = typeof item.valor === 'number' ? item.valor : Number(item.valor) || 0;
            const presentation = Number(item.quantityPresentation) || 1;
            const lineUnit = valor * presentation;

            const minItem = gross * (1 + percentual / 100);
            if (gross <= 0 || lineUnit <= 0) return item;

            const qtyMin = Math.ceil(minItem / lineUnit);
            const currentQty = Number(item.quantity) || 0;
            const currentLine = currentQty * lineUnit;

            if (currentLine >= minItem) return item;

            return { ...item, quantity: qtyMin };
        });
    }

    applyResultToItem(
        item: ItemResponse,
        result: Result,
        campaign: CampaignResponse,
        _context: ApplyContext,
    ): ItemResponse {
        return {
            ...item,
            campaign,
            result,
        };
    }

    recalculateOnQuantityChange(
        campaign: CampaignResponse,
        orderItems: ItemResponse[],
        _context: ApplyContext,
    ): ItemResponse[] {
        const { isMet } = evaluateCondition(campaign, orderItems);
        const resultByNumero = new Map<string, Result>();
        for (const r of campaign.resultados) {
            if (!r.numeroItem) continue;
            resultByNumero.set(r.numeroItem.toLowerCase(), r);
        }

        return orderItems.map((item) => {
            const result = resultByNumero.get((item.number ?? '').toLowerCase());
            if (!result) return item;
            if (item.campaign && item.campaign.id !== campaign.id) return item;

            if (!isMet) {
                if (item.discount === 0 && !item.result) return item;
                return { ...item, discount: 0 };
            }

            const gross = Number(item.valorGross) || 0;
            const percentualCota = Number(result.percentualCota) || 0;
            const meta = gross * (1 + percentualCota / 100);
            const achieved = lineValue(item);
            const desconto = -(Number(result.desconto) || 0);

            const itemMet =
                round2(meta) === 0
                    ? round2(achieved) > 0
                    : round2(achieved) >= round2(meta);

            if (itemMet) {
                return {
                    ...item,
                    discount: desconto,
                    result,
                    campaign,
                };
            }

            return { ...item, discount: 0, result, campaign };
        });
    }

    validateForSubmit(
        campaign: CampaignResponse,
        orderItems: ItemResponse[],
        _context: ApplyContext,
    ): SubmitError[] {
        const { isMet, achievedTotal, minTotal } = evaluateCondition(
            campaign,
            orderItems,
        );
        if (isMet) return [];

        // Identifica itens de condição sem quantidade para mensagem mais clara
        const conditions = getConditionItems(campaign);
        const itemsWithoutQty = conditions.filter((cond) => {
            const item = orderItems.find(
                (i) => i.number?.toLowerCase() === cond.numeroItem?.toLowerCase(),
            );
            return !item || Number(item.quantity) <= 0;
        });

        if (itemsWithoutQty.length > 0 && round2(minTotal) === 0) {
            const itemNumbers = itemsWithoutQty.map((c) => c.numeroItem).join(', ');
            return [
                {
                    code: 'CONDITION_NOT_MET',
                    message: `Campanha "${campaign.nome}": todos os itens de condição devem ter quantidade. Itens pendentes: ${itemNumbers}.`,
                    campaignId: campaign.id,
                },
            ];
        }

        const missing = round2(minTotal - achievedTotal);
        return [
            {
                code: 'CONDITION_NOT_MET',
                message: `Campanha "${campaign.nome}": atinja R$ ${minTotal.toFixed(2)} em itens da condição. Faltam R$ ${missing.toFixed(2)}.`,
                campaignId: campaign.id,
            },
        ];
    }

    requiresExtraFetchParams(
        _campaign: CampaignResponse,
        context: FetchContext,
    ): Partial<FetchParams> {
        const mes = context.orderDate
            ? moment(context.orderDate).month() + 1
            : moment().month() + 1;
        return { mes };
    }

    acceptsPaymentCondition(paymentConditionId: string): boolean {
        return paymentConditionId !== PAYMENT_CONDITION_BONIFICACAO;
    }
}

export const evaluateAtingirMetaCondition = evaluateCondition;
export const getAtingirMetaConditionItems = getConditionItems;
